import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Scope, SessionPrincipal } from '@hrms/shared';
import { api, http, refreshAccessToken, tokenStore } from '@/api/client';

type Status = 'loading' | 'anonymous' | 'authenticated';

export interface AuthContextValue {
  status: Status;
  principal: SessionPrincipal | null;
  /**
   * UX-only permission check (hide/disable). The API enforces every permission independently —
   * never treat this as a security boundary.
   */
  can: (code: string, scope?: Scope) => boolean;
  canAny: (...codes: string[]) => boolean;
  /** Holds the permission beyond SELF (can act on other people). */
  canOthers: (code: string) => boolean;
  login: (email: string, password: string, tenantCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [hasToken, setHasToken] = useState<boolean | null>(null);

  useEffect(() => {
    const unsubscribe = tokenStore.subscribe((authed) => setHasToken(authed));
    // Restore the session from the httpOnly refresh cookie on first load.
    refreshAccessToken().then((t) => setHasToken(!!t));
    return () => {
      unsubscribe();
    };
  }, []);

  const me = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<SessionPrincipal>('/auth/me'),
    enabled: hasToken === true,
    staleTime: 60_000,
    retry: false,
  });

  const status: Status = hasToken === null || (hasToken && me.isLoading) ? 'loading' : hasToken && me.data ? 'authenticated' : 'anonymous';
  const principal = status === 'authenticated' ? (me.data ?? null) : null;

  const can = useCallback(
    (code: string, scope?: Scope) => {
      const scopes = principal?.permissions[code];
      if (!scopes?.length) return false;
      if (!scope) return true;
      // Mirrors the server's scopeCovers(): TENANT covers all, SELF is covered by any scope.
      return scopes.some((s) => s === 'TENANT' || s === scope || scope === 'SELF');
    },
    [principal],
  );
  const canAny = useCallback((...codes: string[]) => codes.some((c) => can(c)), [can]);
  const canOthers = useCallback((code: string) => (principal?.permissions[code] ?? []).some((s) => s !== 'SELF'), [principal]);

  const login = useCallback(
    async (email: string, password: string, tenantCode?: string) => {
      const res = await http.post('/auth/login', { email, password, tenantCode: tenantCode || undefined });
      tokenStore.set(res.data.data.accessToken);
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    [qc],
  );

  const logout = useCallback(async () => {
    try {
      await http.post('/auth/logout', {}, { headers: { 'X-HRMS-CSRF': '1' } });
    } finally {
      tokenStore.set(null);
      qc.clear();
    }
  }, [qc]);

  const switchTenant = useCallback(
    async (tenantId: string) => {
      const res = await api.post<{ accessToken: string }>('/auth/switch-tenant', { tenantId });
      tokenStore.set(res.accessToken);
      qc.clear();
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    [qc],
  );

  const value = useMemo(() => ({ status, principal, can, canAny, canOthers, login, logout, switchTenant }), [status, principal, can, canAny, canOthers, login, logout, switchTenant]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
